import React, { useCallback, useEffect, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import { get } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { useShallow } from 'zustand/react/shallow';

import NotFound from 'assets/svg/emtydata.svg';
import NotificationItem from 'components/NotificationItem/index';
import { MARK_READ_EXIT_DURATION, NAVIGABLE_NOTIFICATION_TYPES } from 'constants/notification';
import { MIN_MD } from 'constants/ThemeSetting';
import { useMarkReadMutation } from 'hooks/features/notification/mutations';
import { useInfiniteNotificationsQuery } from 'hooks/features/notification/queries';
import useNotificationStore from 'store/useNotificationStore';
import {
  getNotificationActionUrl,
  getNotificationId,
  getNotificationProductId,
  getNotificationType,
  isNotificationRead,
  normalizeInternalPath,
} from 'utils/notification';

import { StyledDrawer } from './NotificationDrawer.styled';

/** Distance from the bottom, in px, at which the next unread page is requested. */
const SCROLL_FETCH_THRESHOLD = 48;

function NotificationDrawer({ onClose, visible }) {
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const [markingNotificationId, setMarkingNotificationId] = useState(null);
  const [exitingNotifications, setExitingNotifications] = useState([]);
  const { notifications, unreadCount, setNotifications, appendNotifications, removeNotification } =
    useNotificationStore(
      useShallow(state => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
        setNotifications: state.setNotifications,
        appendNotifications: state.appendNotifications,
        removeNotification: state.removeNotification,
      }))
    );

  const { pages, hasData, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteNotificationsQuery({
    status: 'unread',
  });
  const markReadMutation = useMarkReadMutation();

  useEffect(() => {
    if (!hasData || pages.length === 0) {
      return;
    }

    // Page 1 replaces (so a refetch drops rows the server no longer calls
    // unread); later pages append, and `appendNotifications` dedupes.
    const [firstPage, ...restPages] = pages;

    setNotifications(get(firstPage, 'data', []));
    restPages.forEach(page => appendNotifications(get(page, 'data', [])));
  }, [pages, hasData, setNotifications, appendNotifications]);

  const handleScroll = useCallback(
    event => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

      if (scrollHeight - scrollTop - clientHeight > SCROLL_FETCH_THRESHOLD) {
        return;
      }

      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const handleMarkAsRead = useCallback(
    notification => {
      const notificationId = getNotificationId(notification);

      if (notificationId === null || isNotificationRead(notification) || markReadMutation.isPending) {
        return;
      }

      setMarkingNotificationId(notificationId);
      markReadMutation.mutate(notificationId, {
        onSuccess: () => {
          setExitingNotifications(current =>
            current.some(item => String(getNotificationId(item)) === String(notificationId))
              ? current
              : [...current, notification]
          );

          window.setTimeout(() => {
            removeNotification(notificationId);
            setExitingNotifications(current =>
              current.filter(item => String(getNotificationId(item)) !== String(notificationId))
            );
            setMarkingNotificationId(current => (String(current) === String(notificationId) ? null : current));
          }, MARK_READ_EXIT_DURATION);
        },
        onError: () => {
          setMarkingNotificationId(null);
        },
      });
    },
    [markReadMutation, removeNotification]
  );

  const handleNotificationClick = useCallback(
    notification => {
      const notificationType = getNotificationType(notification);

      if (!NAVIGABLE_NOTIFICATION_TYPES.includes(notificationType)) {
        return;
      }

      const productId = getNotificationProductId(notification);
      const actionUrl = normalizeInternalPath(getNotificationActionUrl(notification));

      if (productId) {
        onClose();
        navigate(`/products/${productId}/edit`);

        return;
      }

      if (actionUrl) {
        onClose();
        navigate(actionUrl);
      }
    },
    [navigate, onClose]
  );

  const renderNotifications = () => {
    const notificationIds = new Set(
      notifications
        .map(getNotificationId)
        .filter(id => id !== null)
        .map(String)
    );
    const displayNotifications = [
      ...notifications,
      ...exitingNotifications.filter(item => {
        const notificationId = getNotificationId(item);

        if (notificationId === null) return true;

        return !notificationIds.has(String(notificationId));
      }),
    ];

    if (displayNotifications.length === 0) {
      return (
        <div className="empty-noti flex justify-center items-center flex-col">
          <img src={NotFound} alt="Không có dữ liệu" />
          <p className="font-semibold text-sm text-grey-900 text-center">Bạn không có thông báo mới nào!</p>
        </div>
      );
    }

    return displayNotifications.map((noti, index) => {
      const notificationId = getNotificationId(noti);
      const productId = getNotificationProductId(noti);
      const actionUrl = normalizeInternalPath(getNotificationActionUrl(noti));
      const notificationType = getNotificationType(noti);
      const canNavigateToProduct =
        NAVIGABLE_NOTIFICATION_TYPES.includes(notificationType) && (Boolean(productId) || Boolean(actionUrl));
      const clickableProps = canNavigateToProduct
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => handleNotificationClick(noti),
            onKeyDown: event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleNotificationClick(noti);
              }
            },
          }
        : {};

      return (
        <div
          key={notificationId !== null ? String(notificationId) : `${noti.created_at || 'notification'}-${index}`}
          className={canNavigateToProduct ? 'cursor-pointer' : 'cursor-default'}
          {...clickableProps}
        >
          <NotificationItem
            notification={noti}
            onRead={handleMarkAsRead}
            readLoading={String(markingNotificationId) === String(notificationId)}
            isExiting={exitingNotifications.some(item => String(getNotificationId(item)) === String(notificationId))}
            showReadTooltip={false}
          />
        </div>
      );
    });
  };

  return (
    <StyledDrawer
      width={width >= MIN_MD ? '308px' : '100%'}
      title={
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold">Thông báo ({unreadCount})</p>
          <CloseOutlined onClick={onClose} />
        </div>
      }
      className="omi-noti-drawer"
      placement="right"
      closable={false}
      onClose={onClose}
      visible={visible}
    >
      <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        {renderNotifications()}
        {isFetchingNextPage && (
          <div className="flex justify-center py-3">
            <Spin size="small" />
          </div>
        )}
      </div>
      {notifications.length > 0 && (
        <div className="mt-auto bg-white py-2 text-center" style={{ borderTop: '1px solid #e5e7eb' }}>
          <Button
            type="link"
            className="text-blue-600 font-semibold"
            block
            onClick={() => {
              onClose();
              navigate('/notifications');
            }}
          >
            Xem tất cả
          </Button>
        </div>
      )}
    </StyledDrawer>
  );
}

export default NotificationDrawer;
